import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  ActionReducerMapBuilder,
  type AsyncThunkOptions,
} from '@reduxjs/toolkit';
import { authService } from '@/services/authService';
import { brandService } from '@/services/brandServices';
import { hashtagCatalogService } from '@/services/hashtagCatalogService';
import { catalogService } from '@/services/catalogService';
import { User, LoginPayload, Brand } from '@/types/auth.types';
import type { HashtagCampaignCatalogItem } from '@/types/hashtag.types';
import type { CategoryFilterCategory } from '@/types/categoryFilters.types';
import type { AudienceFilterItem } from '@/types/audienceFilters.types';
import { HttpError } from '@/errors/HttpError';
import { clearAllTabFilters } from '@/hooks/useTabFilters';
import {
  clearHashtagCampaignsSession,
  upsertHashtagCampaignInSession,
} from '@/utils/hashtagCampaignSession';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { clearActingTenant, readActingTenant } from '@/utils/actingTenant';

// Define la "forma" del estado de autenticación
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  isAuthCheckLoading: boolean;
  brands: Brand[];
  brandsLoading: boolean;
  hashtagCampaigns: HashtagCampaignCatalogItem[];
  hashtagCampaignsLoading: boolean;
  /** true tras una carga exitosa — evita repetir GET en cada pantalla */
  hashtagCampaignsLoaded: boolean;
  hashtagCampaignsUnavailable: boolean;
  categoryFilters: CategoryFilterCategory[];
  categoryFiltersLoading: boolean;
  categoryFiltersLoaded: boolean;
  categoryFiltersUnavailable: boolean;
  audienceFilters: AudienceFilterItem[];
  audienceFiltersLoading: boolean;
  audienceFiltersLoaded: boolean;
  audienceFiltersUnavailable: boolean;
}

interface FetchHashtagCampaignsArg {
  tenantCode: string;
  force?: boolean;
}

interface FetchCategoryFiltersArg {
  tenantCode: string;
  force?: boolean;
}

interface FetchAudienceFiltersArg {
  tenantCode: string;
  force?: boolean;
}

function authErrorMessage(
  error: unknown,
  httpFallback: string,
  unexpectedFallback: string
): string {
  if (error instanceof HttpError) {
    // Prefer a short, sanitized message (never dump HTML/tracebacks from Core).
    return getApiErrorMessage(error, httpFallback);
  }
  return unexpectedFallback;
}

type AuthThunkConfig = {
  rejectValue: string;
};

interface AuthThunkMessages {
  httpFallback: string;
  unexpectedFallback: string;
}

function createAuthThunk<Returned, ThunkArg>(
  typePrefix: string,
  load: (arg: ThunkArg) => Promise<Returned>,
  messages: AuthThunkMessages,
  options?: AsyncThunkOptions<ThunkArg, AuthThunkConfig>
) {
  return createAsyncThunk<Returned, ThunkArg, AuthThunkConfig>(
    typePrefix,
    async (arg, { rejectWithValue }) => {
      try {
        return await load(arg);
      } catch (error: unknown) {
        return rejectWithValue(
          authErrorMessage(error, messages.httpFallback, messages.unexpectedFallback)
        );
      }
    },
    options
  );
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  isAuthCheckLoading: true,
  brands: [],
  brandsLoading: false,
  hashtagCampaigns: [],
  hashtagCampaignsLoading: false,
  hashtagCampaignsLoaded: false,
  hashtagCampaignsUnavailable: false,
  categoryFilters: [],
  categoryFiltersLoading: false,
  categoryFiltersLoaded: false,
  categoryFiltersUnavailable: false,
  audienceFilters: [],
  audienceFiltersLoading: false,
  audienceFiltersLoaded: false,
  audienceFiltersUnavailable: false,
};

export const fetchBrands = createAuthThunk<Brand[], string>(
  'auth/fetchBrands',
  tenantCode => brandService.getBrands(tenantCode),
  {
    httpFallback: 'Could not load brands.',
    unexpectedFallback: 'An unexpected error occurred while loading brands.',
  }
);

export const fetchHashtagCampaigns = createAuthThunk<
  HashtagCampaignCatalogItem[],
  FetchHashtagCampaignsArg
>(
  'auth/fetchHashtagCampaigns',
  ({ tenantCode }) => hashtagCatalogService.getCampaigns(tenantCode),
  {
    httpFallback: 'Could not load hashtag campaigns.',
    unexpectedFallback: 'An unexpected error occurred while loading hashtag campaigns.',
  },
  {
    condition: (arg, { getState }) => {
      const { hashtagCampaignsLoading, hashtagCampaignsLoaded, hashtagCampaignsUnavailable } = (
        getState() as { auth: AuthState }
      ).auth;
      if (hashtagCampaignsLoading) return false;
      if (arg.force === true) return true;
      return !hashtagCampaignsLoaded && !hashtagCampaignsUnavailable;
    },
  }
);

export const fetchCategoryFilters = createAuthThunk<
  CategoryFilterCategory[],
  FetchCategoryFiltersArg
>(
  'auth/fetchCategoryFilters',
  async ({ tenantCode }) => {
    const response = await catalogService.getCategoryFilters(tenantCode);
    return response.categories;
  },
  {
    httpFallback: 'Could not load category filters.',
    unexpectedFallback: 'An unexpected error occurred while loading category filters.',
  },
  {
    condition: (arg, { getState }) => {
      const { categoryFiltersLoading, categoryFiltersLoaded, categoryFiltersUnavailable } = (
        getState() as { auth: AuthState }
      ).auth;
      if (categoryFiltersLoading) return false;
      if (arg.force === true) return true;
      return !categoryFiltersLoaded && !categoryFiltersUnavailable;
    },
  }
);

export const fetchAudienceFilters = createAuthThunk<AudienceFilterItem[], FetchAudienceFiltersArg>(
  'auth/fetchAudienceFilters',
  async ({ tenantCode }) => {
    const response = await catalogService.getAudienceFilters(tenantCode);
    return response.audiences;
  },
  {
    httpFallback: 'Could not load audience filters.',
    unexpectedFallback: 'An unexpected error occurred while loading audience filters.',
  },
  {
    condition: (arg, { getState }) => {
      const { audienceFiltersLoading, audienceFiltersLoaded, audienceFiltersUnavailable } = (
        getState() as { auth: AuthState }
      ).auth;
      if (audienceFiltersLoading) return false;
      if (arg.force === true) return true;
      return !audienceFiltersLoaded && !audienceFiltersUnavailable;
    },
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginPayload, { dispatch, rejectWithValue }) => {
    try {
      clearActingTenant();
      const loginResponse = await authService.login({
        username: credentials.username,
        password: credentials.password,
      });

      if (loginResponse.access_token) {
        localStorage.setItem('accessToken', loginResponse.access_token);
        // Limpiar filtros del usuario anterior
        clearAllTabFilters();
      } else {
        return rejectWithValue('Login failed: No token was provided.');
      }

      const user = await authService.getMe();
      if (user?.tenant_code) {
        dispatch(fetchBrands(user.tenant_code));
      }
      return user;
    } catch (error: unknown) {
      return rejectWithValue(
        authErrorMessage(error, 'Incorrect credentials.', 'An unexpected error occurred.')
      );
    }
  }
);

const ACTING_TENANT_REJECTION_STATUSES = [400, 403, 404];

function isActingTenantRejection(error: unknown): boolean {
  return (
    error instanceof HttpError && ACTING_TENANT_REJECTION_STATUSES.includes(error.response.status)
  );
}

async function getMeForgettingRejectedActingTenant(): Promise<User> {
  try {
    return await authService.getMe();
  } catch (error: unknown) {
    if (!readActingTenant() || !isActingTenantRejection(error)) throw error;
    clearActingTenant();
    return authService.getMe();
  }
}

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { dispatch, rejectWithValue }) => {
    if (!localStorage.getItem('accessToken')) {
      return rejectWithValue('No token found.');
    }

    try {
      const user = await getMeForgettingRejectedActingTenant();
      if (user?.tenant_code) {
        dispatch(fetchBrands(user.tenant_code));
      }
      return user;
    } catch (error: unknown) {
      localStorage.removeItem('accessToken');
      return rejectWithValue(
        authErrorMessage(
          error,
          'Session check failed.',
          'An unexpected error occurred during session check.'
        )
      );
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logout();
    localStorage.removeItem('accessToken');
    clearActingTenant();
    // Limpiar todos los filtros al hacer logout
    clearAllTabFilters();
  } catch (error: unknown) {
    localStorage.removeItem('accessToken');
    clearActingTenant();
    // Limpiar filtros incluso si hay error en el logout
    clearAllTabFilters();
    return rejectWithValue(
      authErrorMessage(error, 'Logout failed.', 'An unexpected error occurred during logout.')
    );
  }
});

function resetHashtagCampaignsState(state: AuthState) {
  state.hashtagCampaigns = [];
  state.hashtagCampaignsLoaded = false;
  state.hashtagCampaignsUnavailable = false;
  clearHashtagCampaignsSession();
}

function resetCategoryFiltersState(state: AuthState) {
  state.categoryFilters = [];
  state.categoryFiltersLoaded = false;
  state.categoryFiltersUnavailable = false;
}

function resetAudienceFiltersState(state: AuthState) {
  state.audienceFilters = [];
  state.audienceFiltersLoaded = false;
  state.audienceFiltersUnavailable = false;
}

function resetLoggedOutState(state: AuthState) {
  state.isAuthenticated = false;
  state.user = null;
  state.brands = [];
  resetHashtagCampaignsState(state);
  resetCategoryFiltersState(state);
  resetAudienceFiltersState(state);
}

function addLoginReducers(builder: ActionReducerMapBuilder<AuthState>) {
  builder
    .addCase(login.pending, state => {
      state.loading = true;
      state.error = null;
    })
    .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
    })
    .addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = action.payload as string;
    });
}

function addCheckAuthReducers(builder: ActionReducerMapBuilder<AuthState>) {
  builder
    .addCase(checkAuth.pending, state => {
      state.isAuthCheckLoading = true;
    })
    .addCase(checkAuth.fulfilled, (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.isAuthCheckLoading = false;
    })
    .addCase(checkAuth.rejected, state => {
      resetLoggedOutState(state);
      state.isAuthCheckLoading = false;
      // Limpiar filtros cuando falla la autenticación
      clearAllTabFilters();
    });
}

function addLogoutReducers(builder: ActionReducerMapBuilder<AuthState>) {
  builder
    .addCase(logoutUser.pending, state => {
      state.loading = true;
    })
    .addCase(logoutUser.fulfilled, state => {
      resetLoggedOutState(state);
      state.loading = false;
      state.error = null;
    })
    .addCase(logoutUser.rejected, (state, action) => {
      resetLoggedOutState(state);
      state.loading = false;
      state.error = action.payload as string;
    });
}

function addCatalogReducers(builder: ActionReducerMapBuilder<AuthState>) {
  builder
    .addCase(fetchBrands.pending, state => {
      state.brandsLoading = true;
    })
    .addCase(fetchBrands.fulfilled, (state, action: PayloadAction<Brand[]>) => {
      state.brands = action.payload;
      state.brandsLoading = false;
    })
    .addCase(fetchBrands.rejected, state => {
      state.brandsLoading = false;
      state.brands = [];
    })
    .addCase(fetchHashtagCampaigns.pending, state => {
      state.hashtagCampaignsLoading = true;
      state.hashtagCampaignsUnavailable = false;
    })
    .addCase(fetchHashtagCampaigns.fulfilled, (state, action) => {
      state.hashtagCampaigns = action.payload;
      state.hashtagCampaignsLoading = false;
      state.hashtagCampaignsLoaded = true;
      state.hashtagCampaignsUnavailable = false;
    })
    .addCase(fetchHashtagCampaigns.rejected, state => {
      state.hashtagCampaignsLoading = false;
      state.hashtagCampaignsLoaded = false;
      state.hashtagCampaignsUnavailable = true;
    })
    .addCase(fetchCategoryFilters.pending, state => {
      state.categoryFiltersLoading = true;
      state.categoryFiltersUnavailable = false;
    })
    .addCase(fetchCategoryFilters.fulfilled, (state, action) => {
      state.categoryFilters = action.payload;
      state.categoryFiltersLoading = false;
      state.categoryFiltersLoaded = true;
      state.categoryFiltersUnavailable = false;
    })
    .addCase(fetchCategoryFilters.rejected, state => {
      state.categoryFiltersLoading = false;
      state.categoryFiltersLoaded = false;
      state.categoryFiltersUnavailable = true;
    })
    .addCase(fetchAudienceFilters.pending, state => {
      state.audienceFiltersLoading = true;
      state.audienceFiltersUnavailable = false;
    })
    .addCase(fetchAudienceFilters.fulfilled, (state, action) => {
      state.audienceFilters = action.payload;
      state.audienceFiltersLoading = false;
      state.audienceFiltersLoaded = true;
      state.audienceFiltersUnavailable = false;
    })
    .addCase(fetchAudienceFilters.rejected, state => {
      state.audienceFiltersLoading = false;
      state.audienceFiltersLoaded = false;
      state.audienceFiltersUnavailable = true;
    });
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetHashtagCampaignsCatalog: state => {
      state.hashtagCampaignsLoaded = false;
      state.hashtagCampaignsUnavailable = false;
    },
    upsertHashtagCampaign: (state, action: PayloadAction<HashtagCampaignCatalogItem>) => {
      const idx = state.hashtagCampaigns.findIndex(
        c => c.hashtag_campaign_code === action.payload.hashtag_campaign_code
      );
      if (idx >= 0) {
        state.hashtagCampaigns[idx] = action.payload;
      } else {
        state.hashtagCampaigns.push(action.payload);
      }
      state.hashtagCampaignsUnavailable = false;
      state.hashtagCampaignsLoaded = true;
      upsertHashtagCampaignInSession(action.payload);
    },
  },
  extraReducers: builder => {
    addLoginReducers(builder);
    addCheckAuthReducers(builder);
    addLogoutReducers(builder);
    addCatalogReducers(builder);
  },
});

export const { resetHashtagCampaignsCatalog, upsertHashtagCampaign } = authSlice.actions;
export default authSlice.reducer;
